import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchGuestCode, resetGuestCode, selectGuestCodeData, selectGuestCodeError, selectGuestCodeStatus, } from '../../store/guestCode';
import { colors } from '../../styles/colors';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Format "123456789" → "123 456 789" */
function formatCode(raw) {
    return raw.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
}
/** Compute remaining seconds from expiresAt ISO string. Returns 0 when expired. */
function getRemainingSeconds(expiresAt) {
    const diff = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
    return Math.max(0, diff);
}
/** Format seconds → "M:SS" */
function formatCountdown(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}
// ---------------------------------------------------------------------------
// Countdown hook
// ---------------------------------------------------------------------------
function useCountdown(expiresAt) {
    const [remaining, setRemaining] = useState(expiresAt ? getRemainingSeconds(expiresAt) : 0);
    useEffect(() => {
        if (!expiresAt) {
            setRemaining(0);
            return undefined;
        }
        setRemaining(getRemainingSeconds(expiresAt));
        const id = setInterval(() => {
            const secs = getRemainingSeconds(expiresAt);
            setRemaining(secs);
            if (secs <= 0)
                clearInterval(id);
        }, 1000);
        return () => clearInterval(id);
    }, [expiresAt]);
    return { remaining, expired: remaining <= 0 };
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function GuestCodeModal({ open, onClose, eventId, matchId }) {
    var _a;
    const dispatch = useAppDispatch();
    const status = useAppSelector(selectGuestCodeStatus);
    const data = useAppSelector(selectGuestCodeData);
    const errorMessage = useAppSelector(selectGuestCodeError);
    const { remaining, expired } = useCountdown((_a = data === null || data === void 0 ? void 0 : data.expiresAt) !== null && _a !== void 0 ? _a : null);
    // Fire the request as soon as the modal opens
    const hasRequestedRef = useRef(false);
    useEffect(() => {
        if (open && !hasRequestedRef.current) {
            hasRequestedRef.current = true;
            dispatch(fetchGuestCode({ eventId, matchId }));
        }
    }, [open, dispatch, eventId, matchId]);
    // Reset state when modal is closed so next open starts fresh
    const handleClose = () => {
        hasRequestedRef.current = false;
        dispatch(resetGuestCode());
        onClose();
    };
    const handleNewCode = () => {
        dispatch(fetchGuestCode({ eventId, matchId }));
    };
    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------
    const isLoading = status === 'loading';
    const isError = status === 'error';
    const isSuccess = status === 'success' && data !== null;
    return (_jsxs(Dialog, Object.assign({ open: open, onClose: handleClose, maxWidth: "sm", fullWidth: true, PaperProps: {
            sx: {
                backgroundColor: colors.surface,
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: '20px',
                color: colors.textPrimary,
                minHeight: 320,
            },
        } }, { children: [_jsxs(DialogTitle, Object.assign({ sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBottom: '8px',
                    borderBottom: `1px solid ${colors.cardBorder}`,
                } }, { children: [_jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: '12px' } }, { children: [_jsx(PersonAddIcon, { sx: { color: colors.primary, fontSize: 28 } }), _jsx(Typography, Object.assign({ variant: "h6", sx: { color: colors.textPrimary, fontWeight: 600 } }, { children: "\u041A\u043E\u0434 \u0434\u043B\u044F \u0433\u043E\u0441\u0442\u044F" }))] })), _jsx(IconButton, Object.assign({ onClick: handleClose, size: "small", sx: { color: colors.textSecondary } }, { children: _jsx(CloseIcon, {}) }))] })), _jsxs(DialogContent, Object.assign({ sx: { paddingTop: '24px', paddingBottom: '8px' } }, { children: [isLoading && (_jsxs(Box, Object.assign({ sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 200,
                            gap: '16px',
                        } }, { children: [_jsx(CircularProgress, { sx: { color: colors.primary } }), _jsx(Typography, Object.assign({ variant: "body2", sx: { color: colors.textSecondary } }, { children: "\u0413\u0435\u043D\u0435\u0440\u0438\u0440\u0443\u0435\u043C \u043A\u043E\u0434\u2026" }))] }))), isError && !isLoading && (_jsxs(Box, Object.assign({ sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 200,
                            gap: '16px',
                            textAlign: 'center',
                        } }, { children: [_jsx(Typography, Object.assign({ variant: "body1", sx: { color: colors.error } }, { children: errorMessage })), _jsx(Button, Object.assign({ variant: "contained", startIcon: _jsx(RefreshIcon, {}), onClick: handleNewCode, sx: {
                                    backgroundColor: colors.primary,
                                    '&:hover': { backgroundColor: colors.primaryDark },
                                } }, { children: "\u041F\u043E\u043F\u0440\u043E\u0431\u043E\u0432\u0430\u0442\u044C \u0435\u0449\u0451 \u0440\u0430\u0437" }))] }))), isSuccess && (_jsxs(Box, Object.assign({ sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '24px',
                        } }, { children: [_jsxs(Box, Object.assign({ sx: { textAlign: 'center' } }, { children: [_jsx(Typography, Object.assign({ variant: "caption", sx: {
                                            color: colors.textSecondary,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '8px',
                                        } }, { children: "\u041A\u043E\u0434 \u0434\u043B\u044F \u0432\u0445\u043E\u0434\u0430" })), _jsx(Typography, Object.assign({ sx: {
                                            fontFamily: 'monospace',
                                            fontSize: expired ? '48px' : '64px',
                                            fontWeight: 700,
                                            color: expired ? colors.textHint : colors.textPrimary,
                                            letterSpacing: '0.08em',
                                            lineHeight: 1,
                                            transition: 'color 0.3s ease',
                                        } }, { children: formatCode(data.code) }))] })), expired ? (_jsx(Typography, Object.assign({ variant: "body1", sx: {
                                    color: colors.error,
                                    fontWeight: 600,
                                    fontSize: '16px',
                                } }, { children: "\u041A\u043E\u0434 \u0443\u0441\u0442\u0430\u0440\u0435\u043B" }))) : (_jsxs(Box, Object.assign({ sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    backgroundColor: colors.surfaceVariant,
                                    border: `1px solid ${colors.cardBorder}`,
                                    borderRadius: '8px',
                                    padding: '8px 20px',
                                } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", sx: { color: colors.textSecondary } }, { children: "\u041E\u0441\u0442\u0430\u043B\u043E\u0441\u044C" })), _jsx(Typography, Object.assign({ sx: {
                                            fontFamily: 'monospace',
                                            fontSize: '20px',
                                            fontWeight: 700,
                                            color: remaining <= 60 ? colors.error : colors.accent,
                                            minWidth: '52px',
                                            textAlign: 'center',
                                        } }, { children: formatCountdown(remaining) }))] })))] })))] })), (isSuccess || isError) && !isLoading && (_jsxs(DialogActions, Object.assign({ sx: {
                    padding: '16px 24px',
                    borderTop: `1px solid ${colors.cardBorder}`,
                    gap: '12px',
                } }, { children: [_jsx(Button, Object.assign({ variant: expired || isError ? 'contained' : 'outlined', startIcon: _jsx(RefreshIcon, {}), onClick: handleNewCode, sx: expired || isError
                            ? {
                                backgroundColor: colors.primary,
                                '&:hover': { backgroundColor: colors.primaryDark },
                                flex: 1,
                            }
                            : {
                                borderColor: colors.cardBorder,
                                color: colors.textSecondary,
                                '&:hover': {
                                    borderColor: colors.textSecondary,
                                    backgroundColor: colors.surfaceVariant,
                                },
                                flex: 1,
                            } }, { children: "\u041D\u043E\u0432\u044B\u0439 \u043A\u043E\u0434" })), _jsx(Button, Object.assign({ variant: "contained", onClick: handleClose, sx: {
                            backgroundColor: expired || isError ? colors.surfaceVariant : colors.primary,
                            color: colors.textPrimary,
                            '&:hover': {
                                backgroundColor: expired || isError ? colors.secondaryLight : colors.primaryDark,
                            },
                            flex: 1,
                        } }, { children: "\u0413\u043E\u0442\u043E\u0432\u043E" }))] })))] })));
}
